import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import { createMemoStyles, defaultBaseline, darken, lighten, defaultFonts, visuallyHidden, clsx, safeRest } from '@wds/utils';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/** Resolves the correct color */

var getMarkedColor = function getMarkedColor(color, palette) {
  if (!color || typeof color !== 'string') {
    return palette.primary.main;
  }

  if (color.startsWith('data-')) {
    return palette.data[color.replace('data-', '')];
  }

  return palette[color].main;
};

var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
      border = theme.border,
      spacing = theme.spacing,
      motion = theme.motion,
      type = theme.type;
  var list = {
    addonIconColor: palette.background.dark,
    affordanceIconColor: palette.secondary.main,
    itemHeight: 52,
    itemMinHeight: 50.25,
    itemDenseMinHeight: 42.25,
    labelFontSize: type.fontSize.bodySm,
    markWidth: 4
  };
  return {
    wrenchList: function wrenchList(_ref) {
      var dense = _ref.dense,
          transparent = _ref.transparent,
          noRules = _ref.noRules;
      return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, defaultBaseline), {}, {
        display: 'block'
      }, dense && {
        '& [class^="wrenchListAddon-"]': {
          paddingLeft: spacing[4],
          '& [class^="wrenchIcons-"]': {
            paddingLeft: 0
          }
        },
        // second className so it needs `*- not ^=` selector
        '& [class*="wrenchListAddonOnRight-"]': {
          paddingRight: spacing[4]
        },
        '& [class^="wrenchListLabel-"]': {
          fontWeight: type.fontWeight.regular,
          margin: [spacing[3] + 1, 0]
        },
        '& [class^="wrenchListItem-"]': {
          minHeight: list.itemDenseMinHeight,
          // this will make it 42.25px (current minimum default)
          borderBottom: 'none'
        }
      }), transparent && {
        '& [class^="wrenchListItem-"]': {
          background: 'none'
        }
      }), noRules && {
        '& [class^="wrenchListRow-"]': {
          borderBottom: 'none'
        }
      });
    },
    wrenchListRow: {
      borderBottom: border.main,
      '&:last-child': {
        borderBottom: 'none'
      },
      'a&': {
        color: palette.text.dark,
        '&:hover, &:focus': {
          textDecoration: 'none'
        }
      }
    },
    wrenchListItem: function wrenchListItem(_ref2) {
      var accordion = _ref2.accordion,
          color = _ref2.color,
          marked = _ref2.marked,
          disabled = _ref2.disabled,
          upgrade = _ref2.upgrade,
          interactive = _ref2.interactive,
          isFocused = _ref2.isFocused;
      return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, defaultBaseline), defaultFonts(theme)), {}, {
        fontWeight: type.fontWeight.medium,
        display: 'flex',
        background: palette.background.light,
        position: 'relative',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: list.itemMinHeight,
        'a&': {
          textDecoration: 'none'
        }
      }, accordion && {
        '&:hover, &:focus, &:active': {
          cursor: 'pointer'
        }
      }), {}, {
        // same specificity as parent List rules
        '[class^="wrenchList-"] &': _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, interactive && _objectSpread(_objectSpread({
          transition: 'background 180ms linear'
        }, isFocused && {
          transition: 'background 125ms linear',
          background: palette.background.accent,
          cursor: 'pointer'
        }), {}, {
          '&:hover, &:focus': {
            transition: 'background 125ms linear',
            background: palette.background.accent,
            cursor: 'pointer'
          },
          '&:active': {
            transition: 'background 125ms linear',
            background: darken(0.05, palette.background.accent)
          }
        })), upgrade && {
          background: lighten(0.42, palette.upgrade.main) // same specificity as transparent List rule

        }), upgrade && interactive && {
          '&:hover, &:focus': {
            background: lighten(0.35, palette.upgrade.main) // same specificity as transparent List rule

          }
        }), disabled && {
          opacity: 0.5,
          pointerEvents: 'none'
        }), (color || marked) && {
          '&:before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: list.markWidth,
            background: getMarkedColor(color, palette)
          }
        })
      });
    },
    wrenchListAddon: function wrenchListAddon() {
      return {
        padding: [0, spacing[3], 0, spacing[4]],
        color: list.addonIconColor,
        display: 'flex',
        alignItems: 'center',
        '> [class^="wrenchIcons-"]': {
          marginLeft: spacing[2]
        }
      };
    },

    /** Additional styles when addon is on the right */
    wrenchListAddonOnRight: function wrenchListAddonOnRight() {
      return {
        paddingLeft: 0,
        paddingRight: spacing[5],
        color: palette.text.lightMuted
      };
    },
    wrenchListAffordance: function wrenchListAffordance(_ref3) {
      var addOnLeft = _ref3.addOnLeft;
      return _objectSpread({
        padding: [0, spacing[5], 0, 0],
        color: list.affordanceIconColor,
        display: 'flex',
        alignItems: 'center',
        '& > *': {
          transition: "transform 150ms ".concat(motion.curve.ease)
        }
      }, addOnLeft && {
        paddingLeft: 0
      });
    },
    wrenchListLabel: function wrenchListLabel(_ref4) {
      var addOnLeft = _ref4.addOnLeft;
      return _objectSpread({
        flexGrow: 1,
        padding: [0, spacing[5]],
        margin: [spacing[4] + 1, 0],
        fontSize: list.labelFontSize,
        lineHeight: 1.25,
        minWidth: 0,
        'a &': {
          color: palette.text.dark
        }
      }, addOnLeft && {
        paddingLeft: 0
      });
    },
    wrenchPaneHide: function wrenchPaneHide(_ref5) {
      var expanded = _ref5.expanded;
      return !expanded && visuallyHidden;
    }
  };
});

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

var SEMANTIC_COLORS = Object.freeze(['primary', 'success', 'info', 'warning', 'upgrade', 'secondary', 'muted']);
Object.freeze(['dark', 'light', 'darkMuted', 'lightMuted', 'link']);
var SIZES = Object.freeze(['lg', 'md', 'sm']);
Object.freeze(['primary', 'upgrade', 'info', 'success', 'warning', 'secondary']);
Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
Object.freeze(['md', 'sm']);
Object.freeze(['success', 'warning']);
Object.freeze(['success', 'warning']);
var DATA_VIZ_THEMES = Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
var KEYS = Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

Object.freeze({
  DOWN: 40,
  END: 35,
  ESC: 27,
  HOME: 36,
  LEFT: 37,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  RIGHT: 39,
  UP: 38,
  ENTER: 13,
  SPACE: 32,
  BACKSPACE: 8,
  TAB: 9
});
Object.freeze({
  37: -1,
  38: -1,
  39: 1,
  40: 1
});
Object.freeze(['top', 'bottom', 'right', 'left']);
Object.freeze(['primary', 'secondary', 'muted', 'success', 'warning', 'upgrade', 'info']);
Object.freeze(['light', 'dark']);
Object.freeze(['primary', 'light', 'dark', 'transparent']);
Object.freeze(['left', 'right', 'even', 'center']);
Object.freeze(['normal', 'near', 'far']);
Object.freeze(['up', 'right', 'down', 'left']);
Object.freeze(['slow', 'normal', 'fast']);
Object.freeze({
  speed: {
    slow: 300,
    normal: 200,
    fast: 100
  }
});
Object.freeze({
  speed: {
    slow: 450,
    normal: 350,
    fast: 250
  },
  distance: {
    normal: 25,
    near: 5,
    far: 75
  }
});
Object.freeze({
  accordion: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A7&scaling=min-zoom&page-id=30%3A2',
  'action-sheet': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A293&scaling=min-zoom&page-id=30%3A288',
  alert: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A317&scaling=min-zoom&page-id=30%3A312',
  avatar: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A293&scaling=min-zoom&page-id=4%3A288',
  badge: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A1243&scaling=min-zoom&page-id=4%3A1238',
  banner: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A341&scaling=min-zoom&page-id=30%3A336',

  /**
   * TODO: Currently goes to Global Padding, change to box when created
   */
  box: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A942&scaling=min-zoom&page-id=30%3A937',
  button: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=20%3A9&scaling=min-zoom&page-id=20%3A4',
  'button-group': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A389&scaling=min-zoom&page-id=30%3A384',
  card: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A437&scaling=min-zoom&page-id=30%3A432',
  checkbox: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A1037&scaling=min-zoom&page-id=4%3A1032',
  coachmark: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A413&scaling=min-zoom&page-id=30%3A408',
  'form-group': '',
  grid: '',
  icons: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=895%3A13856&scaling=min-zoom&page-id=119%3A2',
  input: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A485&scaling=min-zoom&page-id=30%3A480',

  /**
   * TODO: This doesn't look like it's up to date right now
   */
  'input-group': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A509&scaling=min-zoom&page-id=30%3A504',
  link: '',
  list: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A893&scaling=min-zoom&page-id=30%3A888',
  menu: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A533&scaling=min-zoom&page-id=30%3A528',
  modal: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A557&scaling=min-zoom&page-id=30%3A552',
  motion: '',
  multiselect: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A581&scaling=min-zoom&page-id=30%3A576',
  'number-picker': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A629&scaling=min-zoom&page-id=30%3A624',
  'page-header': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A653&scaling=min-zoom&page-id=30%3A648',
  pagination: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A677&scaling=min-zoom&page-id=30%3A672',
  popout: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A701&scaling=min-zoom&page-id=30%3A696',
  'progress-bar': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A715&scaling=min-zoom&page-id=4%3A710',
  'progress-circle': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A715&scaling=min-zoom&page-id=4%3A710',
  radio: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A1037&scaling=min-zoom&page-id=4%3A1032',

  /**
   * TODO: Doesn't look complete yet, update if necessary
   */
  responsive: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=304%3A1897&scaling=min-zoom&page-id=160%3A17',
  select: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A725&scaling=min-zoom&page-id=30%3A720',
  'select-menu': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A725&scaling=min-zoom&page-id=30%3A720',
  sheet: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A749&scaling=min-zoom&page-id=30%3A744',
  slider: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A773&scaling=min-zoom&page-id=30%3A768',
  switch: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A1037&scaling=min-zoom&page-id=4%3A1032',
  table: '',
  tabs: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A797&scaling=min-zoom&page-id=30%3A792',
  textarea: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A821&scaling=min-zoom&page-id=30%3A816',
  toast: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A845&scaling=min-zoom&page-id=30%3A840',
  tooltip: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A869&scaling=min-zoom&page-id=30%3A864',
  typography: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=18%3A8&scaling=min-zoom&page-id=18%3A3'
});

var _excluded = ["addOnLeft", "addOnRight", "affordance", "children", "color", "component", "disabled", "id", "index", "isFocused", "isNavigable", "interactive", "marked", "navigationTarget", "onClick", "upgrade"],
    _excluded2 = ["accordion", "expanded", "paneComponent"];
var ListItem = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var addOnLeft = _ref.addOnLeft,
      addOnRight = _ref.addOnRight,
      affordance = _ref.affordance,
      children = _ref.children,
      color = _ref.color,
      component = _ref.component,
      disabled = _ref.disabled,
      id = _ref.id,
      index = _ref.index,
      isFocused = _ref.isFocused,
      isNavigable = _ref.isNavigable,
      interactive = _ref.interactive,
      marked = _ref.marked,
      navigationTarget = _ref.navigationTarget,
      onClick = _ref.onClick,
      upgrade = _ref.upgrade,
      other = _objectWithoutProperties(_ref, _excluded);

  // no reason to expose these in props
  var accordion = other.accordion,
      expanded = other.expanded,
      paneComponent = other.paneComponent,
      rest = _objectWithoutProperties(other, _excluded2);

  var theme = useTheme();

  var _useStyles = useStyles({
    accordion: accordion,
    color: color,
    marked: marked,
    disabled: disabled,
    upgrade: upgrade,
    isFocused: isFocused,
    interactive: interactive,
    addOnLeft: addOnLeft,
    addOnRight: addOnRight,
    expanded: expanded,
    theme: theme
  }),
      wrenchListRow = _useStyles.wrenchListRow,
      wrenchListItem = _useStyles.wrenchListItem,
      wrenchListLabel = _useStyles.wrenchListLabel,
      wrenchListAddon = _useStyles.wrenchListAddon,
      wrenchListAddonOnRight = _useStyles.wrenchListAddonOnRight,
      wrenchListAffordance = _useStyles.wrenchListAffordance;

  var addOnClassNames = clsx(wrenchListAddon, wrenchListAddonOnRight);
  var href = isNavigable ? navigationTarget : undefined;
  var ListItemWrapper = isNavigable ? component || 'a' : 'div';
  var interactiveProps = interactive ? {
    tabIndex: disabled ? '-1' : 0,
    role: 'button'
  } : {};

  var onHandleKeyDown = function onHandleKeyDown(e) {
    if (onClick && (e.key === KEYS.ENTER || e.key === KEYS.SPACE)) {
      onClick(e);
    }
  };

  return /*#__PURE__*/React.createElement("li", {
    className: wrenchListRow
  }, /*#__PURE__*/React.createElement(ListItemWrapper, Object.assign({
    disabled: disabled,
    className: wrenchListItem,
    onClick: onClick,
    onKeyDown: onHandleKeyDown,
    href: href,
    id: id,
    "data-index": index,
    ref: ref
  }, interactiveProps, safeRest(rest)), addOnLeft ? /*#__PURE__*/React.createElement("div", {
    className: wrenchListAddon
  }, addOnLeft) : null, /*#__PURE__*/React.createElement("span", {
    className: wrenchListLabel
  }, children), addOnRight ? /*#__PURE__*/React.createElement("div", {
    className: addOnClassNames
  }, addOnRight) : null, affordance ? /*#__PURE__*/React.createElement("span", {
    className: wrenchListAffordance
  }, affordance) : null), typeof expanded !== 'undefined' ? paneComponent : null);
});
ListItem.propTypes = {
  /**
   * To be used for Accordion Item
   */
  accordion: PropTypes.bool,

  /**
   * Left addons are used to support the label by supplying it more context and/or faster scanability
   * for users. Left addons must be consistent across an entire list. So if you add an avatar to one,
   * you must add an avatar to the rest.',
   */
  addOnLeft: PropTypes.node,

  /**
   * Right addons are used for secondary actions that can be taken or elements that provide a hint of extra content.
   * Unlike left addons, right addons do not need to persist throughout an entire list.
   * So if you use a button for a right addon you do not have to have one for each list item in the list.
   */
  addOnRight: PropTypes.node,

  /**
   * Add a custom affordance to the right of the ListItem
   */
  affordance: PropTypes.node,

  /**
   * Content of the ListItem
   */
  children: PropTypes.node,

  /**
   * Color variants for List Item
   */
  color: PropTypes.oneOfType([PropTypes.oneOf(SEMANTIC_COLORS), PropTypes.oneOf(DATA_VIZ_THEMES)]),

  /**
   * In combination with `isNavigable` you can pass a custom component that will be
   * rendered in place of a regular a element. By default it is `a` if isNaviagable is true
   */
  component: PropTypes.func,

  /**
   * If true, disable specific ListItem
   */
  disabled: PropTypes.bool,

  /**
   * Identifier for ListItem component
   */
  id: PropTypes.string,

  /**
   * Data index of current ListItem
   */
  index: PropTypes.number,

  /**
   * If true, make a ListItem interactive enables hover and press-states. Most often only used in menus.
   */
  interactive: PropTypes.bool,

  /**
   * If true, ListItem will be focused on mount
   */
  isFocused: PropTypes.bool,

  /**
   * If true, ListItem can be a navigational component
   */
  isNavigable: PropTypes.bool,

  /**
   * If true, add a mark to the left-hand side of a list item.
   * Take notice that the label does not shift to accommodate for the mark; it stays in its original location.
   */
  marked: PropTypes.bool,

  /**
   * The href location of your nav target
   */
  navigationTarget: PropTypes.string,

  /**
   * onClick handler for ListItem
   * function(event: object) => void
   */
  onClick: PropTypes.func,

  /**
   * Add special "upgrade" highlight styles to item.
   */
  upgrade: PropTypes.bool
};
ListItem.defaultProps = {
  accordion: false,
  addOnLeft: undefined,
  addOnRight: undefined,
  affordance: undefined,
  children: undefined,
  color: undefined,
  component: undefined,
  disabled: false,
  id: undefined,
  index: undefined,
  interactive: false,
  isFocused: false,
  isNavigable: false,
  marked: false,
  // eslint-disable-next-line no-script-url
  navigationTarget: 'javascript:void(0)',
  onClick: undefined,
  upgrade: false
}; // The displayName is needed for component's Accordion and perhaps other consumers in the future
// https://reactjs.org/docs/forwarding-refs.html#displaying-a-custom-name-in-devtools

ListItem.displayName = 'ListItem';
ListItem.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "ListItem",
  "props": {
    "accordion": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "To be used for Accordion Item"
    },
    "addOnLeft": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Left addons are used to support the label by supplying it more context and/or faster scanability\nfor users. Left addons must be consistent across an entire list. So if you add an avatar to one,\nyou must add an avatar to the rest.',"
    },
    "addOnRight": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Right addons are used for secondary actions that can be taken or elements that provide a hint of extra content.\nUnlike left addons, right addons do not need to persist throughout an entire list.\nSo if you use a button for a right addon you do not have to have one for each list item in the list."
    },
    "affordance": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Add a custom affordance to the right of the ListItem"
    },
    "children": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Content of the ListItem"
    },
    "color": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "union",
        "value": [{
          "name": "enum",
          "computed": true,
          "value": "SEMANTIC_COLORS"
        }, {
          "name": "enum",
          "computed": true,
          "value": "DATA_VIZ_THEMES"
        }]
      },
      "required": false,
      "description": "Color variants for List Item"
    },
    "component": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "In combination with `isNavigable` you can pass a custom component that will be\nrendered in place of a regular a element. By default it is `a` if isNaviagable is true"
    },
    "disabled": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "If true, disable specific ListItem"
    },
    "id": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Identifier for ListItem component"
    },
    "index": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Data index of current ListItem"
    },
    "interactive": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "If true, make a ListItem interactive enables hover and press-states. Most often only used in menus."
    },
    "isFocused": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "If true, ListItem will be focused on mount"
    },
    "isNavigable": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "If true, ListItem can be a navigational component"
    },
    "marked": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "If true, add a mark to the left-hand side of a list item.\nTake notice that the label does not shift to accommodate for the mark; it stays in its original location."
    },
    "navigationTarget": {
      "defaultValue": {
        "value": "'javascript:void(0)'",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "The href location of your nav target"
    },
    "onClick": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "onClick handler for ListItem\nfunction(event: object) => void"
    },
    "upgrade": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Add special \"upgrade\" highlight styles to item."
    }
  }
};

var List = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var dense = _ref.dense,
      interactive = _ref.interactive,
      noRules = _ref.noRules,
      transparent = _ref.transparent,
      children = _ref.children,
      role = _ref.role,
      id = _ref.id;
  var theme = useTheme();

  var _useStyles = useStyles({
    dense: dense,
    interactive: interactive,
    noRules: noRules,
    transparent: transparent,
    theme: theme
  }),
      wrenchList = _useStyles.wrenchList;

  var newChildren = React.Children.map(children, function (child, index) {
    return /*#__PURE__*/React.isValidElement(child) && /*#__PURE__*/React.cloneElement(child, {
      interactive: interactive,
      index: index
    });
  });
  return /*#__PURE__*/React.createElement("ul", {
    ref: ref,
    className: wrenchList,
    role: role,
    id: id
  }, newChildren);
});
List.propTypes = {
  /**
   * Composed of List.Item
   */
  children: PropTypes.node.isRequired,

  /**
   * Reduce child List.Item height and font-weight
   */
  dense: PropTypes.bool,

  /**
   * Unique identifier for list
   */
  id: PropTypes.string,

  /**
   * Add hover style to all children List.Item,
   */
  interactive: PropTypes.bool,

  /**
   * Remove divider lines between List.Item
   */
  noRules: PropTypes.bool,

  /**
   * Define aria role - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/List_role
   */
  role: PropTypes.string,

  /**
   * Removed background color from child List.Item,
   */
  transparent: PropTypes.bool
};
List.defaultProps = {
  dense: false,
  id: undefined,
  interactive: false,
  noRules: false,
  role: undefined,
  transparent: false
};
List.displayName = 'List';
List.Item = ListItem;
List.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "List",
  "props": {
    "dense": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Reduce child List.Item height and font-weight"
    },
    "id": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Unique identifier for list"
    },
    "interactive": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Add hover style to all children List.Item,"
    },
    "noRules": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Remove divider lines between List.Item"
    },
    "role": {
      "defaultValue": {
        "value": "undefined",
        "computed": true
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Define aria role - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/List_role"
    },
    "transparent": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Removed background color from child List.Item,"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "Composed of List.Item"
    }
  }
};

export { List };
